/**
 * Périmètre d'un objectif « exercices » : packs, SKILLCARDS et exercices reliés
 * (migration 0514). Calculé côté serveur :
 *   - `get_objective_scope(id)` : tout le détail d'UN objectif (page objectif) ;
 *   - `get_my_objectives_progress()` : la progression de TOUS mes objectifs en
 *     une requête (tableau de bord).
 * RPC postérieures aux types générés → client générique.
 */

import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  objectiveRingFromCards,
  type ObjectiveCardsProgress,
  type ObjectiveScopeRule,
} from '@jeraisonne/db/objectives';
import { supabase } from '@/lib/supabase';
import { normalizeStudentSkillcard, type StudentSkillcard } from '@/lib/cards/student-skillcards';
import type { ObjectiveProgress } from './objective-types';

const db = supabase as unknown as SupabaseClient;

export interface ObjectiveScopePack {
  id: string;
  slug: string;
  title: string;
  hook: string | null;
  subject: string;
  level: string;
  color: string | null;
  icon: string;
  start_month: number;
  end_month: number | null;
}

export interface ObjectiveScopeExercise {
  id: string;
  title: string;
  difficulty: 'easy' | 'medium' | 'hard';
  level: string | null;
  split_part: number | null;
  card_id: string;
  illustration_url: string | null;
}

export interface ObjectiveScopeCard extends StudentSkillcard {
  pack_id: string | null;
}

export interface ObjectiveScope {
  rule: ObjectiveScopeRule;
  packs: ObjectiveScopePack[];
  cards: ObjectiveScopeCard[];
  exercises: ObjectiveScopeExercise[];
  progress: ObjectiveCardsProgress;
}

const EMPTY_PROGRESS: ObjectiveCardsProgress = {
  card_count: 0,
  mastered_count: 0,
  avg_mastery: 0,
  hora: 0,
};

/** Réponse brute → périmètre normalisé (tolérant à un champ manquant). */
export function normalizeObjectiveScope(raw: unknown): ObjectiveScope {
  const r = (raw ?? {}) as Record<string, unknown>;
  const arr = (v: unknown) => (Array.isArray(v) ? (v as Record<string, unknown>[]) : []);
  return {
    rule: (typeof r.rule === 'string' ? r.rule : 'programme') as ObjectiveScopeRule,
    packs: arr(r.packs) as unknown as ObjectiveScopePack[],
    cards: arr(r.cards).map((c) => ({
      ...normalizeStudentSkillcard(c),
      pack_id: typeof c.pack_id === 'string' ? c.pack_id : null,
    })),
    exercises: arr(r.exercises) as unknown as ObjectiveScopeExercise[],
    progress: { ...EMPTY_PROGRESS, ...((r.progress as ObjectiveCardsProgress) ?? {}) },
  };
}

/** Progression des cartes → `ObjectiveProgress` (anneau, libellés). */
export function progressFromCards(
  objectiveId: string,
  templateType: string,
  cards: ObjectiveCardsProgress,
  packTitles: string[] = [],
): ObjectiveProgress {
  const ring = objectiveRingFromCards(cards);
  return {
    objectiveId,
    templateType,
    kind: 'cards',
    totalExercises: 0,
    completedExercises: 0,
    completionPercent: ring.completionPercent,
    masteryPercent: ring.masteryPercent,
    cards,
    packTitles,
  };
}

export const objectiveScopeKey = (objectiveId: string) => ['objective-scope', objectiveId] as const;

export async function fetchObjectiveScope(objectiveId: string): Promise<ObjectiveScope> {
  const { data, error } = await db.rpc('get_objective_scope', { p_objective_id: objectiveId });
  if (error) throw new Error(error.message);
  return normalizeObjectiveScope(data);
}

export function useObjectiveScope(objectiveId: string | null, enabled = true) {
  return useQuery({
    queryKey: objectiveScopeKey(objectiveId ?? ''),
    enabled: enabled && !!objectiveId,
    queryFn: () => fetchObjectiveScope(objectiveId!),
  });
}

export interface MyObjectiveProgressRow {
  objective_id: string;
  rule: ObjectiveScopeRule;
  pack_titles: string[];
  progress: ObjectiveCardsProgress;
}

export async function fetchMyObjectivesProgress(): Promise<MyObjectiveProgressRow[]> {
  const { data, error } = await db.rpc('get_my_objectives_progress');
  if (error) throw new Error(error.message);
  return ((data ?? []) as MyObjectiveProgressRow[]).map((row) => ({
    ...row,
    pack_titles: Array.isArray(row.pack_titles) ? row.pack_titles : [],
    progress: { ...EMPTY_PROGRESS, ...(row.progress ?? {}) },
  }));
}
